/**
 * Opens a single WebSocket connection to /api/ws for the authenticated user
 * and invalidates TanStack-Query caches when the backend pushes "invalidate"
 * events (e.g. the Telegram/WhatsApp bot persisted new items, or the daily
 * reminder job ran).
 *
 * Auto-reconnect with exponential backoff capped at 30s. No-ops if the user
 * isn't authenticated yet.
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore } from "../lib/api";

export function useRealtimeSync(enabled) {
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const reconnectRef = useRef({ attempt: 0, timer: null });

  useEffect(() => {
    if (!enabled) return undefined;

    const token = tokenStore.get();
    if (!token) return undefined;

    const httpUrl = process.env.REACT_APP_BACKEND_URL || "";
    if (!httpUrl) return undefined;
    const wsBase = httpUrl.replace(/^http/, "ws");
    const url = `${wsBase}/api/ws?token=${encodeURIComponent(token)}`;

    let closed = false;

    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          reconnectRef.current.attempt = 0;
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg?.event === "invalidate") {
              // Scope-aware invalidation: only refetch what actually changed.
              // The backend sends scope="transactions" | "wealth" |
              // "transactions+wealth" | "all".
              const scope = msg.scope || "all";
              const parts = scope.split("+");
              if (scope === "all") {
                qc.invalidateQueries();
              } else {
                if (parts.includes("transactions")) {
                  qc.invalidateQueries({ queryKey: ["dashboard"] });
                  qc.invalidateQueries({ queryKey: ["movements"] });
                  qc.invalidateQueries({ queryKey: ["receivables"] });
                  qc.invalidateQueries({ queryKey: ["payments"] });
                  qc.invalidateQueries({ queryKey: ["history"] });
                  qc.invalidateQueries({ queryKey: ["report"] });
                  qc.invalidateQueries({ queryKey: ["net-worth-series"] });
                  qc.invalidateQueries({ queryKey: ["reminders"] });
                }
                if (parts.includes("wealth")) {
                  qc.invalidateQueries({ queryKey: ["dashboard"] });
                  qc.invalidateQueries({ queryKey: ["investments"] });
                  qc.invalidateQueries({ queryKey: ["debts"] });
                  qc.invalidateQueries({ queryKey: ["goals"] });
                  qc.invalidateQueries({ queryKey: ["net-worth-series"] });
                  qc.invalidateQueries({ queryKey: ["report"] });
                }
              }
            } else if (msg?.event === "billing.composition_changed") {
              // iter-168: lifecycle state change (past_due, pending_cancellation,
              // pending_downgrade, refund, etc.) — refresh anything billing-related
              // so the LifecycleBanner + plan card re-render with the new state
              // immediately, no manual reload needed.
              qc.invalidateQueries({ queryKey: ["billing-me"] });
              qc.invalidateQueries({ queryKey: ["billing-my-history"] });
              qc.invalidateQueries({ queryKey: ["billing-my-packs"] });
            }
          } catch (err) {
            // Malformed frame — log in dev so we notice schema drift.
            if (process.env.NODE_ENV !== "production") {
              console.warn("WS frame parse failed", err);
            }
          }
        };
        ws.onerror = () => {
          // The browser fires both onerror and onclose; we handle the retry in onclose.
        };
        ws.onclose = () => {
          if (closed) return;
          const attempt = (reconnectRef.current.attempt += 1);
          const delay = Math.min(30_000, 1000 * Math.pow(1.7, attempt));
          reconnectRef.current.timer = setTimeout(connect, delay);
        };
      } catch (e) {
        console.warn("WS connect failed", e);
      }
    };

    connect();

    return () => {
      closed = true;
      clearTimeout(reconnectRef.current.timer);
      if (wsRef.current && wsRef.current.readyState <= 1) {
        try {
          wsRef.current.close();
        } catch {
          /* ignore */
        }
      }
      wsRef.current = null;
    };
  }, [enabled, qc]);
}
